import type { AppUnlock } from "@iyk/music-domain"
import {
  type AdminInvite,
  type AdminUser,
  type ContentUnlock,
  type Sweepstake,
  type SweepstakeWinner,
  type User,
  type World
} from "@iyk/music-domain/client"
import { AppRedirectRoute, AppRoute } from "@iyk/react-router/client"

type Slug = World.Slug

export const AppRoutes = {
  home: AppRoute("/"),

  // users
  verifyOtp: AppRedirectRoute("/verify-otp"),

  login: AppRedirectRoute("/login"),
  logout: AppRoute("/logout"),

  settings: AppRoute("/settings"),

  resendOtp: AppRoute("/resend-otp"),

  inviteOnly: AppRoute("/invite-only"),

  acceptInvite: (token: AdminInvite.Token) => AppRoute("/invite/:token/accept", { token: token.value }),

  api: {
    users: {
      uploads: {
        createAvatarUpload: AppRoute("/api/users/uploads/avatar")
      }
    },
    sweepstakes: {
      drawWinners: (slug: Slug, sweepstakeId: Sweepstake.Id) =>
        AppRoute(`/a/:slug/api/sweepstakes/:sweepstakeId/draw-winners`, {
          slug,
          sweepstakeId
        }),
      batchMessageWinners: (slug: Slug, sweepstakeId: Sweepstake.Id) =>
        AppRoute(`/a/:slug/api/sweepstakes/:sweepstakeId/batch-message-winners`, { slug, sweepstakeId })
    }
  },

  worlds: {
    index: AppRoute("/worlds"),
    getStarted: AppRoute("/worlds/get-started"),
    new: AppRoute("/worlds/new"),

    home: (slug: Slug) => AppRoute(`/a/:slug`, { slug }),
    settings: {
      team: {
        index: (slug: Slug) => AppRoute(`/a/:slug/settings/team`, { slug }),
        invite: (slug: Slug) => AppRoute(`/a/:slug/settings/team/invites`, { slug }),
        revokeInvite: (slug: Slug, inviteId: string) =>
          AppRoute(`/a/:slug/settings/team/invites/:inviteId/revoke`, { slug, inviteId }),
        removeMember: (slug: Slug, userId: AdminUser.Id) =>
          AppRoute(`/a/:slug/settings/team/members/:userId/remove`, { slug, userId })
      }
    },
    profile: (slug: Slug) => AppRoute(`/a/:slug/profile`, { slug }),

    onboarding: {
      saveAndExit: (slug: Slug) => AppRoute(`/a/:slug/onboarding/save-and-exit`, { slug }),
      basics: (slug: Slug) => AppRoute(`/a/:slug/onboarding/basics`, { slug }),
      assets: (slug: Slug) => AppRoute(`/a/:slug/onboarding/assets`, { slug }),
      worldId: (slug: Slug) => AppRoute(`/a/:slug/onboarding/world-id`, { slug }),
      links: (slug: Slug) => AppRoute(`/a/:slug/onboarding/links`, { slug }),
      policies: (slug: Slug) => AppRoute(`/a/:slug/onboarding/policies`, { slug })
    },

    items: {
      index: (slug: Slug) => AppRoute(`/a/:slug/items`, { slug }),

      products: {
        index: (slug: Slug) => AppRoute(`/a/:slug/items/products`, { slug }),
        detail: (slug: Slug, id: string) => AppRoute(`/a/:slug/items/products/:id`, { slug, id }),
        new: (slug: Slug) => AppRoute(`/a/:slug/items/products/new`, { slug }),
        edit: (slug: Slug, id: string) => AppRoute(`/a/:slug/items/products/:id/edit`, { slug, id }),
        preview: (slug: Slug, id: string) => AppRoute(`/a/:slug/items/products/:id/preview`, { slug, id })
      },
      occProducts: {
        index: (slug: Slug) => AppRoute(`/a/:slug/items/occ-products`, { slug }),
        detail: (slug: Slug, id: string) => AppRoute(`/a/:slug/items/occ-products/:id`, { slug, id }),
        preview: (slug: Slug, id: string) => AppRoute(`/a/:slug/items/occ-products/:id/preview`, { slug, id })
      },
      sonoProducts: {
        index: (slug: Slug) => AppRoute(`/a/:slug/items/sono-products`, { slug }),
        detail: (slug: Slug, id: string) => AppRoute(`/a/:slug/items/sono-products/:id`, { slug, id })
      }
    },

    fans: {
      index: (slug: Slug) => AppRoute(`/a/:slug/fans`, { slug }),
      detail: (slug: Slug, userId: User.Id) => AppRoute(`/a/:slug/fans/:userId`, { slug, userId })
    },

    unlocks: {
      index: (slug: Slug) => AppRoute(`/a/:slug/unlocks`, { slug }),

      apps: {
        index: (slug: Slug) => AppRoute(`/a/:slug/unlocks/apps`, { slug }),
        detail: (slug: Slug, id: AppUnlock.Id) => AppRoute(`/a/:slug/unlocks/apps/:id`, { slug, id })
      },

      content: {
        index: (slug: Slug) => AppRoute(`/a/:slug/unlocks/content`, { slug }),
        new: (slug: Slug) => AppRoute(`/a/:slug/unlocks/content/new`, { slug }),
        detail: (slug: Slug, id: ContentUnlock.Id) => AppRoute(`/a/:slug/unlocks/content/:id`, { slug, id }),
        edit: (slug: Slug, id: ContentUnlock.Id) =>
          AppRoute(`/a/:slug/unlocks/content/:id/edit`, { slug, id }),
        requiredProofs: {
          edit: (slug: Slug, id: ContentUnlock.Id) =>
            AppRoute(`/a/:slug/unlocks/content/:id/required-proofs/edit`, { slug, id })
        }
      }
    },

    shows: {
      index: (slug: Slug) => AppRoute(`/a/:slug/shows`, { slug }),
      new: (slug: Slug) => AppRoute(`/a/:slug/shows/new`, { slug }),
      detail: (slug: Slug, id: string) => AppRoute(`/a/:slug/shows/:id`, { slug, id }),
      edit: (slug: Slug, id: string) => AppRoute(`/a/:slug/shows/:id/edit`, { slug, id }),
      preview: (slug: Slug, id: string) => AppRoute(`/a/:slug/shows/:id/preview`, { slug, id }),
      sweepstake: (slug: Slug, id: string, sweepstakeId: Sweepstake.Id) =>
        AppRoute(`/a/:slug/shows/:id/sweepstakes/:sweepstakeId`, { slug, id, sweepstakeId }),
      sweepstakeConversation: (
        slug: Slug,
        id: string,
        sweepstakeId: Sweepstake.Id,
        winnerId: SweepstakeWinner.Id
      ) =>
        AppRoute(`/a/:slug/shows/:id/sweepstakes/:sweepstakeId/conversation/:winnerId`, {
          slug,
          id,
          sweepstakeId,
          winnerId
        })
    },

    api: {
      worlds: {
        uploads: {
          createImageUpload: (slug: Slug) => AppRoute(`/a/:slug/api/worlds/uploads/image`, { slug })
        }
      },
      shows: {
        uploads: {
          createProofImageUpload: (slug: Slug) => AppRoute(`/a/:slug/api/shows/uploads/proof-image`, { slug })
        }
      },
      products: {
        uploads: {
          createPrimaryMediaUpload: (slug: Slug) =>
            AppRoute(`/a/:slug/api/products/uploads/primary-media`, { slug })
        }
      },
      unlocks: {
        content: {
          uploads: {
            createMediaUpload: (slug: Slug) =>
              AppRoute(`/a/:slug/api/unlocks/content/uploads/media`, { slug })
          }
        }
      }
    }
  }
}
